import {
  Unifree,
  useMask,
  ScannerModal,
  Icon
} from '@yes.technology/react-toolkit'
import { ComponentProps, useRef } from 'react'
import { defer } from 'lodash'
import useRedundantState from '../../hooks/useRedundantState'
import useIsOpen from 'modules/shared/hooks/useIsOpen'
import ActionButton from 'modules/shared/ActionButton'
import styled from 'styled-components'
import { ActionButton as TActionButton, StatusClass } from 'types/shared'
import DataFieldActionButton from './DataFieldActionButton'

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: flex-end;
  grid-gap: 0.25rem;
`

type Props = {
  id: string
  label: string
  informational?: boolean
  required?: boolean
  value?: string
  onChange: (value: string) => void
  helpText?: string
  statusClass?: StatusClass
  explanationText?: string
  formatMask?: string
  isFetchingBlueprints?: boolean
  disabled?: boolean
  enableQRCodeReader?: boolean
  actionButton?: TActionButton
  enableBarcodeReader?: boolean
  scannerFormats?: string[]
}

function ActivityInputString({
  id,
  label,
  informational,
  required,
  value,
  onChange,
  helpText,
  statusClass,
  explanationText,
  formatMask,
  isFetchingBlueprints = false,
  disabled,
  enableQRCodeReader = false,
  actionButton,
  enableBarcodeReader = false,
  scannerFormats
}: Props) {
  const { applyMask } = useMask()
  const { redundantValue, setRedundantValue } = useRedundantState({ value })
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    isOpen: isScannerOpen,
    close: onCloseScanner,
    open: onOpenScanner
  } = useIsOpen()

  const handleDataFieldChange = (rawValue: string) => {
    const formattedValue = formatMask
      ? applyMask(rawValue, formatMask)
      : rawValue

    setRedundantValue(formattedValue)
    onChange(formattedValue)
  }

  const onScanSuccess = (content: string) => {
    if (content) {
      handleDataFieldChange(content)

      defer(() => {
        inputRef.current?.focus?.()
      })
    }

    onCloseScanner()
  }

  return (
    <>
      {(enableQRCodeReader || enableBarcodeReader) && (
        <ScannerModal
          isOpen={isScannerOpen}
          onClose={onCloseScanner}
          onScanSuccess={onScanSuccess}
          acceptedFormats={
            scannerFormats as ComponentProps<
              typeof ScannerModal
            >['acceptedFormats']
          }
          format={enableBarcodeReader ? 'barcode' : 'qrcode'}
        />
      )}
      <Container>
        <Unifree
          ref={inputRef}
          label={label}
          id={id}
          value={redundantValue}
          informational={informational}
          required={!!required}
          statusClass={informational ? 'default' : statusClass}
          onChange={handleDataFieldChange}
          placeholder={helpText || explanationText}
          aria-label={label}
          isLoadingBlueprints={isFetchingBlueprints}
          disabled={disabled}
          actionButton={
            actionButton ? (
              <DataFieldActionButton
                actionButton={actionButton}
                disabled={disabled}
              />
            ) : null
          }
        />
        {(enableQRCodeReader || enableBarcodeReader) && (
          <ActionButton onClick={() => onOpenScanner()} variant='secondary'>
            <Icon
              iconName={enableBarcodeReader ? 'Barcode' : 'QRCodeReader'}
              icSize='semiSmall'
            />
          </ActionButton>
        )}
      </Container>
    </>
  )
}

export default ActivityInputString
